import React, { useEffect, useState } from "react";
import { Plus, SquarePen, Trash, Search, ListRestart } from "lucide-react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  ModalCloseButton,
  IconButton,
  Text
} from "@chakra-ui/react";
import { toast } from "react-toastify";
import { getDmsStatusAll } from "../../../redux/reduxApi/DmsReduxApi";
import { useDispatch, useSelector } from "react-redux";
import { pageData } from "../../../utils/paginationConfig";
import { deleteDmsStatus } from "../../../redux/api/dms/DmsStatusApi";
import { deleteDmsStatusAction } from "../../../redux/reduxSlice/DmsStatusSlice";
import Pagination from "../../../utils/pagination/Pagination";

function DmsMasterList() {
  const dispatch = useDispatch();

  const [dmsMasterData, setDmsMasterData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const getDmsStatusData = useSelector((state) => state.dmsStatus.dmsStatus);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  console.log("data", data);

  // Get permissions from localStorage
  const permissions = localStorage.getItem("permissions");
  const filterData = permissions ? JSON.parse(permissions) : [];

  // Check permissions
  const canAdd = filterData?.find(mod => mod.moduleName === "DMS" && mod.add === true);
  const canDelete = filterData?.find(mod => mod.moduleName === "DMS" && mod.delete === true);
  const canModify = filterData?.find(mod => mod.moduleName === "DMS" && mod.modifyAll === true);

  // Fetch data on component mount and when currentPage changes
  useEffect(() => {
    fetchDmsMaster();
  }, [currentPage]);

  // Filter data when search term changes
  useEffect(() => {
    if (searchTerm) {
      const filtered = dmsMasterData.filter(
        (item) =>
          item.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.fileSiteNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.netMeteringAppNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.otherAppType?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
      setTotalCount(filtered.length);
      setTotalPages(Math.ceil(filtered.length / 30));
    } else {
      setFilteredData(dmsMasterData);
      setTotalCount(dmsMasterData.length);
      setTotalPages(Math.ceil(dmsMasterData.length / 30));
    }
  }, [searchTerm, dmsMasterData]);

  const fetchDmsMaster = async () => {
    setLoading(true);
    try {
      const response = await dispatch(
        getDmsStatusAll({ page: currentPage, limit: 30, size: 30 })
      );
      
      if (response.payload?.data?.data) {
        const apiData = response.payload.data.data;
        setDmsMasterData(apiData);
        setFilteredData(apiData);
        setData(apiData);
        setTotalCount(apiData.length);
        setTotalPages(Math.ceil(apiData.length / 30));
      } else if (response.payload?.data) {
        setDmsMasterData(response.payload.data);
        setFilteredData(response.payload.data);
        setData(response.payload.data);
        setTotalCount(response.payload.data.length);
        setTotalPages(Math.ceil(response.payload.data.length / 30));
      }
    } catch (error) {
      console.error("Error fetching DMS master data:", error);
      toast.error("Failed to fetch DMS master data");
      setDmsMasterData([]);
      setFilteredData([]);
      setData([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setItemToDelete(null);
    setDeleteId(null);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!itemToDelete?.id) {
      toast.error("Missing DMS Master ID");
      return;
    }

    setDeleteId(itemToDelete.id);
    setIsDeleting(true);

    try {
      // Use the actual delete API
      const response = await deleteDmsStatus(itemToDelete.id);
      
      if (response.data?.success) {
        toast.success(response.data.message || "DMS record deleted successfully");
        
        // Update the local state
        setDmsMasterData((prev) => prev.filter((item) => item.id !== itemToDelete.id));
        setFilteredData((prev) => prev.filter((item) => item.id !== itemToDelete.id));
        setData((prev) => prev.filter((item) => item.id !== itemToDelete.id));
        
        // Update Redux state
        dispatch(deleteDmsStatusAction(itemToDelete.id));
        
        // Update counts
        setTotalCount(prev => prev - 1);
        
      } else {
        toast.error(response.data?.message || "Failed to delete DMS record");
      }
    } catch (error) {
      console.error("Error deleting DMS record:", error);
      toast.error("Failed to delete DMS record");
    } finally {
      setIsDeleting(false);
      closeDeleteModal();
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle reset search
  const handleReset = () => {
    setSearchTerm("");
    fetchDmsMaster();
  };

  // Calculate display data for current page
  const displayData = filteredData.slice(
    (currentPage - 1) * 30,
    currentPage * 30
  );

  return (
    <>
      {loading && (
        <div className="spinner-overlay">
          <Spinner size="xl" color="pink.500" />
        </div>
      )}
      
      {/* Header with Search and Add Button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <InputGroup width="300px">
            <InputLeftElement pointerEvents="none">
              <Search size={18} color="#666" />
            </InputLeftElement>
            <Input
              placeholder="Search by customer, site no, application no..."
              value={searchTerm}
              onChange={handleSearch}
              className="rounded"
            />
          </InputGroup>
          
          {searchTerm && (
            <IconButton
              icon={<ListRestart size={20} />}
              onClick={handleReset}
              variant="ghost"
              size="sm"
              aria-label="Reset search"
            />
          )}
        </div>
        
        {canAdd && (
          <button
            className="new-button"
            onClick={() => toast.info("Add functionality to be implemented")}
          >
            <Plus size="18px" />
            <span> Add </span>
          </button>
        )}
      </div>
      
      <div style={{ minHeight: "auto" }}>
        <div
          className="custom-scroll"
          style={{ minHeight: "auto", overflowX: "auto" }}
        >
          <TableContainer className="card p-0 card-detail mt-1"
            style={{ height: "70vh", position: "relative", overflowY: "scroll", scrollbarColor: "#A0AEC0 #EDF2F7", scrollbarWidth: "10px" }}

          >
            <Table variant="simple" size="sm">
              <Thead  style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 1300,
                              backgroundColor: "white",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                            >
                <Tr className="tr-border">
                  {canDelete && <Th className="py-1 th-color">Actions</Th>}
                  <Th className="py-1 th-color">File Site No.</Th>
                  <Th className="py-1 th-color">Customer Name</Th>
                  <Th className="py-1 th-color">Other App Type</Th>
                  <Th className="py-1 th-color">Other App Date</Th>
                  <Th className="py-1 th-color">Net Metering App No</Th>
                  <Th className="py-1 th-color">Net Metering Apply Date</Th>
                  <Th className="py-1 th-color">Net Metering Remark</Th>
                  <Th className="py-1 th-color">SCO</Th>
                  <Th className="py-1 th-color">SCO Remark</Th>
                  <Th className="py-1 th-color">Net Metering Install Date</Th>
                  <Th className="py-1 th-color">National Portal Applied Date</Th>
                  <Th className="py-1 th-color">Subsidy Redeem Date</Th>
                  <Th className="py-1 th-color">Subsidy Disbursed Date</Th>
                </Tr>
              </Thead>
              <Tbody>
                {displayData && displayData.length > 0 ? (
                  displayData.map((item) => (
                    <Tr className="tr-border" key={item.id}>
                      {/* Actions Column */}
                      {canDelete && (
                        <Td>
                          <div className="d-flex">
                            {canDelete && (
                              <i className="px-1">
                                <Trash
                                  size={16}
                                  style={{ cursor: "pointer" }}
                                  strokeWidth="1"
                                  onClick={() => openDeleteModal(item)}
                                  color="#ff176b"
                                />
                              </i>
                            )}
                            {/* {canModify && (
                              <i className="px-1">
                                <SquarePen
                                  size={16}
                                  style={{ cursor: "pointer" }}
                                  color="#ff176b"
                                  onClick={() =>
                                    toast.info("Edit functionality to be implemented")
                                  }
                                />
                              </i>
                            )} */}
                          </div>
                        </Td>
                      )}

                      {/* File Site No. */}
                      <Td className="font-colors">
                        {item.fileSiteNo || "N/A"}
                      </Td>

                      {/* Customer Name */}
                      <Td className="font-colors">
                        {item.customerName || "N/A"}
                      </Td>

                      {/* Other Application Type */}
                      <Td className="font-colors">
                        {item.otherAppType || "N/A"}
                      </Td>

                      {/* Other Application Date */}
                      <Td className="font-colors">
                        {formatDate(item.otherAppDate)}
                      </Td>

                      {/* Net Metering Application Number */}
                      <Td className="font-colors">
                        {item.netMeteringAppNo || "N/A"}
                      </Td>

                      {/* Net Metering Apply Date */}
                      <Td className="font-colors">
                        {formatDate(item.netMeteringApplyingDate)}
                      </Td>

                      {/* Net Metering Apply Remark */}
                      <Td className="font-colors">
                        <div
                          style={{
                            maxWidth: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.scoRemark || "N/A"}
                        </div>
                      </Td>

                      {/* SCO - Service Connection Order */}
                      <Td className="font-colors">
                        {formatDate(item.serviceConnectionOrder)}
                      </Td>

                      {/* SCO Remark */}
                      <Td className="font-colors">
                        <div
                          style={{
                            maxWidth: "150px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.scoRemark || "N/A"}
                        </div>
                      </Td>

                      {/* Net Metering Install Date */}
                      <Td className="font-colors">
                        {formatDate(item.netMeteringInstallationDate)}
                      </Td>

                      {/* National Portal Applied Date */}
                      <Td className="font-colors">
                        {formatDate(item.nationalPortalApplyDate)}
                      </Td>

                      {/* Subsidy Redeem Date */}
                      <Td className="font-colors">
                        {formatDate(item.subsidyRedeemDate)}
                      </Td>

                      {/* Subsidy Disbursed Date */}
                      <Td className="font-colors">
                        {formatDate(item.subsidyDisbursedDate)}
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td
                      colSpan={canDelete ? 14 : 13}
                      className="text-center py-4"
                    >
                      {loading ? "Loading..." : "No DMS master records found"}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination Footer */}
        <div className="footer d-flex fixed-bottom start-20">
          <div
            className="total"
            style={{ marginLeft: "16%", marginTop: "15px" }}
          >
            <p className="fw-semibold paginationCount">
              Total Count: {totalCount}
              {searchTerm && ` (Filtered from ${dmsMasterData.length})`}
              {` | Page ${currentPage} of ${totalPages}`}
            </p>
          </div>
          
          {/* Pagination Component */}
          <div className="d-flex align-items-center justify-content-center mx-auto">
            <Pagination 
              totalPages={totalPages} 
              onPageChange={setCurrentPage} 
              currentPage={currentPage}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={closeDeleteModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Confirmation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete this DMS record?
              {itemToDelete && (
                <>
                  <br />
                  <br />
                  <strong>File Site No:</strong> {itemToDelete.fileSiteNo || "N/A"}
                  <br />
                  <strong>Customer Name:</strong> {itemToDelete.customerName || "N/A"}
                </>
              )}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              className="create-button"
              mr={3}
              onClick={handleDeleteConfirm}
              isLoading={isDeleting && deleteId === itemToDelete?.id}
              loadingText="Deleting..."
            >
              Delete
            </Button>
            <Button className="cancel-button" onClick={closeDeleteModal}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default DmsMasterList;